import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useMetaTags } from '@/hooks/useMetaTags';
import { useToast } from '@/hooks/use-toast';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import PageHeader from '@/components/PageHeader';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Trash2, MoreHorizontal, X } from 'lucide-react';
import { optimizeCloudinaryUrl, deleteMediaById } from '@/lib/utils';
import { useAlbumNavigation } from "@/hooks/useAlbumNavigation";
import { safeLogRocketTrack } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { AlbumDownloadButton } from '@/components/AlbumDownloadButton';
import { useSettings } from '@/hooks/useSettings';
import { useAlbumInteractions } from '@/contexts/AlbumInteractionsContext';
import { useTheme } from '@/contexts/ThemeProvider';
import { MediaGrid, MediaInfoModal } from '@/components/media-grid';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SortByUploadedByDropdown } from '@/components/SortByUploadedByDropdown';
import { getSurfaceContrastColors, toSurfaceContrastVariables } from '@/lib/contrastUtils';


interface MediaItem {
  id: string;
  secure_url: string;
  cdn_thumb?: string;
  cdn_display_s?: string;
  cdn_display_m?: string;
  created_at?: string;
  inserted_at?: string;
  votes_count: number;
  comments_count?: number;
  uploaded_by?: string;
  uploader_name?: string;
  uploader_avatar?: string;
  uploader_email?: string;
  flagged?: boolean;
  description?: string;
}

type MediaVotesRow = {
  id: string;
  secure_url: string;
  cdn_thumb?: string | null;
  cdn_display_s?: string | null;
  cdn_display_m?: string | null;
  created_at?: string | null;
  inserted_at?: string | null;
  votes_count?: number | null;
  comments_count?: number | null;
  uploaded_by?: string | null;
  flagged?: boolean | null;
  description?: string | null;
};

type UserDisplayInfoRow = {
  id: string;
  display_name?: string | null;
  avatar_url?: string | null;
  email?: string | null;
};

type SortOption = 'votes' | 'recent' | 'comments' | 'flagged';

const Fotos = () => {
  const { albumId } = useAlbumContext();
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  const [, setDeleting] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const { getAlbumPath } = useAlbumNavigation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const { getBooleanSetting } = useSettings();
  const { registerMediaItems, getVotesCount, getCommentsCount } = useAlbumInteractions();

  const { toast } = useToast();

  useMetaTags({
    title: t('adminPhotosMetaTitle'),
    pathname: '/admin/fotos'
  });


  const fetchMediaItems = async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    try {
      if (!silent) setLoading(true);

      // Fetch all media with votes (including flagged)
      const { data: mediaData, error: mediaError } = await supabase
        .rpc('get_media_with_votes_filtered', { p_album_id: albumId, p_exclude_flagged: false } as never);

      if (mediaError) throw mediaError;

      const mediaRows = (mediaData || []) as MediaVotesRow[];
      const userIds = Array.from(new Set(mediaRows.map((m) => m.uploaded_by).filter(Boolean)));

      // Fetch uploader display info (name, avatar, email)
      let usersInfo: UserDisplayInfoRow[] = [];
      if (userIds.length > 0) {
        const { data: users, error: usersError } = await supabase.rpc('get_user_display_info', { user_ids: userIds, include_email: true } as never);
        if (!usersError && users) usersInfo = users;
      }
      const userMap = new Map(usersInfo.map((u) => [u.id, u]));

      const itemsWithUserData: MediaItem[] = mediaRows.map((row) => {
        const u = userMap.get(row.uploaded_by);
        const uploader_name = u?.display_name || 'Usuario eliminado';
        const uploader_avatar = u?.avatar_url || '';
        const uploader_email = u?.email || '';
        return {
          id: row.id,
          secure_url: row.secure_url,
          cdn_thumb: row.cdn_thumb || optimizeCloudinaryUrl({ originalUrl: row.secure_url, width: 100 }),
          cdn_display_s: row.cdn_display_s || optimizeCloudinaryUrl({ originalUrl: row.secure_url, width: 600 }),
          cdn_display_m: row.cdn_display_m || optimizeCloudinaryUrl({ originalUrl: row.secure_url, width: 1200 }),
          created_at: row.created_at || row.inserted_at,
          inserted_at: row.inserted_at,
          votes_count: row.votes_count || 0,
          comments_count: row.comments_count ?? 0,
          uploaded_by: row.uploaded_by,
          uploader_name,
          uploader_avatar,
          uploader_email,
          description: row.description || undefined,
          flagged: row.flagged || false,
        };
      });

      // Do not sort here; just set raw items. Sorting is handled client-side without refetch.
      setMediaItems(itemsWithUserData);
    } catch (error) {
      console.error('Error fetching media items:', error);
      toast({
        title: t('error'),
        description: t('failedToLoadPhotos'),
        variant: "destructive",
      });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchMediaItems();
  }, [albumId]);

  useEffect(() => {
    registerMediaItems(mediaItems);
  }, [mediaItems, registerMediaItems]);


  const handleSelectItem = (itemId: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;

    setDeleting(true);

    // Optimistic UI: remove selected items immediately
    const ids = Array.from(selectedItems);
    const previousItems = mediaItems;
    setMediaItems(prev => prev.filter(item => !selectedItems.has(item.id)));

    try {
      const results = await Promise.allSettled(
        ids.map(async (id) => {
          await deleteMediaById(id);
        })
      );

      const failedIds: string[] = [];
      results.forEach((r, idx) => {
        if (r.status === 'rejected') failedIds.push(ids[idx]);
      });

      if (failedIds.length > 0) {
        // Reinsert failed ones
        setMediaItems(previousItems.filter(item => !selectedItems.has(item.id) || failedIds.includes(item.id)));

        toast({
          title: t('error'),
          description: `No se pudieron eliminar ${failedIds.length} fotos`,
          variant: 'destructive',
        });

        // Keep failed ones selected for retry
        setSelectedItems(new Set(failedIds));
      } else {
        toast({
          title: t('success'),
          description: t('photosDeletedSuccessfully', { count: ids.length }),
        });
        setSelectedItems(new Set());
      }

      safeLogRocketTrack("Fotos eliminada desde panel admin", {
        cantidad: ids.length - failedIds.length,
        is_admin: true,
        user_id: user.id
      });

      // Silent refresh to stay consistent without page jump
      fetchMediaItems({ silent: true });
    } catch (error) {
      console.error('Error deleting media items:', error);
      // Revert optimistic update on unexpected error
      setMediaItems(previousItems);
      setSelectedItems(new Set(ids));
      toast({
        title: t('error'),
        description: "No se pudieron eliminar las fotos seleccionadas",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleSingleDelete = async (itemId: string) => {
    // Optimistic UI: remove from list immediately to avoid table jump
    const previous = mediaItems;
    setMediaItems(prev => prev.filter(item => item.id !== itemId));

    try {
      await deleteMediaById(itemId);

      toast({
        title: t('success'),
        description: t('photoDeletedSuccessfully'),
      });

      safeLogRocketTrack("Foto eliminada desde panel admin", {
        foto_id: itemId,
        is_admin: true,
        user_id: user.id
      });

      // Silent refresh to keep data consistent without triggering global loading state
      fetchMediaItems({ silent: true });
    } catch (error) {
      console.error('Error deleting media item:', error);
      // Revert optimistic update
      setMediaItems(previous);
      toast({
        title: t('error'),
        description: t('couldNotDeletePhoto'),
        variant: "destructive",
      });
    }
  };

  const handleToggleFlag = async (itemId: string, currentFlaggedState: boolean) => {
    // Optimistic update - immediately update the UI
    const newFlaggedState = !currentFlaggedState;
    setMediaItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? { ...item, flagged: newFlaggedState }
          : item
      )
    );

    try {
      const { error } = await supabase.rpc('toggle_media_flagged', {
        media_uuid: itemId
      });

      if (error) throw error;

      toast({
        title: t('success'),
        description: newFlaggedState ? t('photoFlaggedForModeration') : t('photoUnflaggedSuccessfully'),
      });

      // LogRocket event for flag/unflag
      safeLogRocketTrack(newFlaggedState ? "Foto marcada para moderación" : "Foto aprobada", {
        foto_id: itemId,
        is_admin: true,
        user_id: user.id
      });

    } catch (error) {
      console.error('Error toggling flag:', error);

      // Revert optimistic update on error
      setMediaItems(prevItems =>
        prevItems.map(item =>
          item.id === itemId
            ? { ...item, flagged: currentFlaggedState }
            : item
        )
      );

      toast({
        title: t('error'),
        description: t('couldNotChangeModerationStatus'),
        variant: "destructive",
      });
    }
  };



  const enableAlbumDownload = getBooleanSetting('enable_album_download', false);
  const isCommentsEnabled = getBooleanSetting('enable_comments', false);
  const isLikesEnabled = getBooleanSetting('enable_likes', false);
  const isModerationEnabled = getBooleanSetting('enable_moderation', false);
  const totalUnflaggedMedia = mediaItems.filter(item => !item.flagged).length;

  const PAGE_SIZE = 40;
  const [page, setPage] = useState(1);

  const uniqueUsers = useMemo(() => {
    const map = new Map<string, { id: string; name: string; avatar?: string | null }>();
    mediaItems.forEach(it => {
      if (it.uploaded_by && it.uploader_name) {
        map.set(it.uploaded_by, { id: it.uploaded_by, name: it.uploader_name, avatar: it.uploader_avatar });
      }
    });
    return Array.from(map.values());
  }, [mediaItems]);

  const filteredItemsForGrid = useMemo(() => {
    if (selectedUsers.size === 0) return mediaItems;
    return mediaItems.filter((item) => item.uploaded_by && selectedUsers.has(item.uploaded_by));
  }, [mediaItems, selectedUsers]);
  const canSortByVotes = useMemo(
    () =>
      isLikesEnabled &&
      filteredItemsForGrid.some((item) => getVotesCount(item.id, item.votes_count || 0) > 0),
    [isLikesEnabled, filteredItemsForGrid, getVotesCount]
  );
  const canSortByComments = useMemo(
    () =>
      isCommentsEnabled &&
      filteredItemsForGrid.some((item) => getCommentsCount(item.id, item.comments_count || 0) > 0),
    [isCommentsEnabled, filteredItemsForGrid, getCommentsCount]
  );
  const hasAdditionalSortOptions = canSortByVotes || canSortByComments || isModerationEnabled;
  const adminPhotosDropdownTriggerClass =
    'h-9 text-xs text-foreground [&>span]:text-xs [&_svg]:text-foreground [&_svg]:opacity-100';
  const uploadedByDropdownClass =
    '[&_button]:h-9 [&_button]:text-xs [&_button]:text-foreground [&_button>svg]:text-foreground [&_button>svg]:opacity-100';
  const cardSurface = tokens.colors.card || tokens.colors.background || '0 0% 100%';
  const cardText = tokens.colors['card-foreground'] || tokens.colors.foreground;
  const cardSurfaceStyle = useMemo<React.CSSProperties>(
    () => toSurfaceContrastVariables(getSurfaceContrastColors(cardSurface, cardText)) as React.CSSProperties,
    [cardSurface, cardText],
  );

  const displayItems = useMemo(() => {
    const arr = [...filteredItemsForGrid];
    return arr.sort((a, b) => {
      if (sortBy === 'votes') {
        const votesDiff = getVotesCount(b.id, b.votes_count || 0) - getVotesCount(a.id, a.votes_count || 0);
        if (votesDiff !== 0) return votesDiff;
        return new Date(b.created_at || b.inserted_at || '').getTime() - new Date(a.created_at || a.inserted_at || '').getTime();
      } else if (sortBy === 'comments') {
        const commentsDiff =
          getCommentsCount(b.id, b.comments_count || 0) - getCommentsCount(a.id, a.comments_count || 0);
        if (commentsDiff !== 0) return commentsDiff;
        return new Date(b.created_at || b.inserted_at || '').getTime() - new Date(a.created_at || a.inserted_at || '').getTime();
      } else if (sortBy === 'flagged') {
        if (a.flagged === b.flagged) {
          return new Date(b.created_at || b.inserted_at || '').getTime() - new Date(a.created_at || a.inserted_at || '').getTime();
        }
        return (b.flagged ? 1 : 0) - (a.flagged ? 1 : 0);
      }
      return new Date(b.created_at || b.inserted_at || '').getTime() - new Date(a.created_at || a.inserted_at || '').getTime();
    });
  }, [filteredItemsForGrid, sortBy, getVotesCount, getCommentsCount]);

  useEffect(() => {
    if (sortBy === 'votes' && !canSortByVotes) {
      setSortBy('recent');
      return;
    }
    if (sortBy === 'comments' && !canSortByComments) {
      setSortBy('recent');
      return;
    }
    if (sortBy === 'flagged' && !isModerationEnabled) {
      setSortBy('recent');
    }
  }, [sortBy, canSortByVotes, canSortByComments, isModerationEnabled]);

  // Reset pagination when sorting or filters change
  useEffect(() => {
    setPage(1);
  }, [sortBy, selectedUsers]);

  const paginatedItems = useMemo(() => {
    return displayItems.slice(0, page * PAGE_SIZE);
  }, [displayItems, page]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="" />
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground">{t('loadingPhotos')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t('adminPanelTitle')} />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={getAlbumPath("/admin")}>{t('adminPanelTitle')}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t('photosTitle')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-6">
          {/* Title on its own line */}
          <div className="w-full" style={{ paddingTop: 'var(--heading-top-offset, 0px)' }}>
            <h1 className="inline-flex max-w-full items-baseline gap-2 text-2xl font-bold leading-none text-foreground">
              <span>{t('photosTitle')}</span>
              <span>({mediaItems.length})</span>
            </h1>
          </div>

          {/* Actions under the title */}
          <div className="mt-4 flex flex-col gap-3 w-full">
            {/* Mobile top row: filter left, download right */}
            <div className="flex sm:hidden items-center gap-2 flex-wrap">
              <SortByUploadedByDropdown
                users={uniqueUsers}
                selected={selectedUsers}
                onChange={setSelectedUsers}
                className={uploadedByDropdownClass}
              />
              {enableAlbumDownload && (
                <div className="ml-auto">
                  <AlbumDownloadButton totalAssets={totalUnflaggedMedia} />
                </div>
              )}
            </div>

            {/* Mobile second row: sort on left, selection toggle on right */}
            <div className="flex sm:hidden items-center gap-3 w-full">
              <div>
                {hasAdditionalSortOptions && (
                  <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                    <SelectTrigger className={`w-48 ${adminPhotosDropdownTriggerClass}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recent">{t('newest')}</SelectItem>
                      {canSortByVotes && <SelectItem value="votes">{t('mostVotedFirst')}</SelectItem>}
                      {canSortByComments && <SelectItem value="comments">{t('mostCommentedFirst')}</SelectItem>}
                      {isModerationEnabled && <SelectItem value="flagged">{t('flaggedFirst')}</SelectItem>}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="ml-auto">
                <Button variant="ghost" size="sm" className="px-0 hover:bg-transparent" onClick={() => selectionMode ? setSelectionMode(false) : setSelectionMode(true)}>
                  {selectionMode ? t('cancel') : t('selectPhotos')}
                </Button>
              </div>
            </div>

            {/* Desktop row: uploaded-by first, then sort, then download; selection toggle on right */}
            <div className="hidden sm:flex items-center gap-3 w-full">
              <div className="flex items-center gap-2">
                <SortByUploadedByDropdown
                  users={uniqueUsers}
                  selected={selectedUsers}
                  onChange={setSelectedUsers}
                  className={uploadedByDropdownClass}
                />

                {hasAdditionalSortOptions && (
                  <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                    <SelectTrigger className={`w-48 ${adminPhotosDropdownTriggerClass}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recent">{t('newest')}</SelectItem>
                      {canSortByVotes && <SelectItem value="votes">{t('mostVotedFirst')}</SelectItem>}
                      {canSortByComments && <SelectItem value="comments">{t('mostCommentedFirst')}</SelectItem>}
                      {isModerationEnabled && <SelectItem value="flagged">{t('flaggedFirst')}</SelectItem>}
                    </SelectContent>
                  </Select>
                )}
                {enableAlbumDownload && <AlbumDownloadButton totalAssets={totalUnflaggedMedia} />}
              </div>
              <div className="ml-auto">
                <Button variant="ghost" size="sm" className="px-0 hover:bg-transparent" onClick={() => selectionMode ? setSelectionMode(false) : setSelectionMode(true)}>
                  {selectionMode ? t('cancel') : t('selectPhotos')}
                </Button>
              </div>
            </div>
            {/* Selected users display */}
            {selectedUsers.size > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {Array.from(selectedUsers).map(userId => {
                  const user = uniqueUsers.find(u => u.id === userId);
                  if (!user) return null;
                  return (
                    <div key={userId} className="flex items-center gap-2 bg-muted rounded-full px-3 py-1">
                      {user.avatar ? (
                        <img src={user.avatar || ''} alt={user.name} className="w-5 h-5 rounded-full object-cover" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-background flex items-center justify-center text-xs">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm">{user.name}</span>
                      <button
                        onClick={() => {
                          setSelectedUsers(prev => {
                            const next = new Set(prev);
                            next.delete(userId);
                            return next;
                          });
                        }}
                        className="ml-1 hover:bg-background rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {mediaItems.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('noPhotosFound')}</p>
          </div>
        ) : (
          <div
            className="bg-card rounded-lg border shadow-sm p-3 md:p-4 text-[hsl(var(--surface-fg))]"
            style={cardSurfaceStyle}
          >
            <MediaGrid
              items={paginatedItems}
              onTileClick={(item) => { if (!selectionMode) { setSelectedMedia(item); setIsPanelOpen(true); } }}
              selectionMode={selectionMode}
              selectedIds={selectedItems}
              onToggleSelect={(id) => handleSelectItem(id, !selectedItems.has(id))}
              showComments={isCommentsEnabled}
              showLikes={isLikesEnabled}
              gridClassName="grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-6"
            />
            <div className="flex items-center justify-center sm:justify-between mt-4">
              <div className="text-sm text-[hsl(var(--surface-muted-fg))]">
                {`Mostrando ${paginatedItems.length} de ${displayItems.length}`}
              </div>
              {paginatedItems.length < displayItems.length && (
                <Button variant="outline" onClick={() => setPage((p) => p + 1)}>{t('loadMore')}</Button>
              )}
            </div>
          </div>
        )}

        {/* Side Panel for media details and actions */}
        <MediaInfoModal
          open={isPanelOpen}
          onOpenChange={(open) => {
            setIsPanelOpen(open);
            if (!open) setSelectedMedia(null);
          }}
          item={selectedMedia}
          items={displayItems}
          currentIndex={selectedMedia ? displayItems.findIndex(item => item.id === selectedMedia.id) : -1}
          onNavigate={(index) => {
            if (index >= 0 && index < displayItems.length) {
              setSelectedMedia(displayItems[index]);
            }
          }}
          onToggleFlag={(item) => {
            handleToggleFlag(item.id, !!item.flagged);
            // Update selectedMedia state for immediate UI feedback
            setSelectedMedia((prev) => (prev && prev.id === item.id ? { ...prev, flagged: !prev.flagged } : prev));
          }}
          onSingleDelete={(item) => {
            handleSingleDelete(item.id);
            setIsPanelOpen(false);
            setSelectedMedia(null);
          }}
        />

        {/* Selection bottom bar */}
        {selectionMode && (
          <div className="fixed inset-x-0 bottom-0 z-40  bg-foreground text-background shadow-lg animate-in slide-in-from-bottom-2">
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium">{t('selectedCount', { count: selectedItems.size })}</span>
              <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="text-background hover:text-background/90 hover:bg-transparent" onClick={() => setSelectedItems(new Set(displayItems.map(i => i.id)))}>{t('selectAll')}</Button>

                  {selectedItems.size > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="flex items-center gap-2 border-background bg-background text-foreground hover:bg-background/70">
                          <span className="hidden sm:inline">{t('actions')}</span>
                          <span className="sm:hidden"><MoreHorizontal className="w-4 h-4" /></span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent side="top" align="end">
                        <DropdownMenuItem onClick={handleBulkDelete}>
                          {t('delete')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={async () => {
                          const ids = Array.from(selectedItems);
                          if (ids.length === 0) return;
                          // Optimistic update
                          const previous = mediaItems;
                          setMediaItems(prev => prev.map(it => ids.includes(it.id) ? { ...it, flagged: true } : it));
                          try {
                            const toFlag = previous.filter(it => ids.includes(it.id) && !it.flagged).map(it => it.id);
                            await Promise.allSettled(toFlag.map(id => supabase.rpc('toggle_media_flagged', { media_uuid: id })));
                            toast({ title: t('success'), description: t('photoFlaggedForModeration') });
                          } catch (e) {
                            setMediaItems(previous);
                            toast({ title: t('error'), description: t('couldNotChangeModerationStatus'), variant: 'destructive' });
                          }
                        }}>
                          {t('hide')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Fotos;
